import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { odczytajAktualnyDrukWToku, wyczyscDrukWToku } from '../services/fiscal/drukWToku';

/**
 * Pytanie kasjera po restarcie strony w trakcie druku paragonu.
 *
 * Przebieg (raz, gdy POS jest gotowy - zalogowany kasjer z dostepem):
 *   1. localStorage ma znacznik "druk w toku" (nieprzeterminowany)? Nie -> koniec.
 *   2. GET /pos/cart/<id> -> pos_transakcje.fiskalizacja.
 *      - 1 (potwierdzenie jednak doszlo)      -> wyczysc znacznik, nic nie pokazuj
 *      - 404 (transakcji nie ma)               -> wyczysc znacznik, nic nie pokazuj
 *      - inny blad (padl internet)             -> zostaw znacznik, pytanie wroci
 *      - 0 albo -1 (brak potwierdzenia)        -> pokaz okno
 *   3. Decyzja kasjera -> PUT /transactions/<id>/fiscal-status z zrodlo
 *      'kasjer_po_restarcie' (serwer dopisuje slad do uwag i audit_log):
 *      - "Tak"     -> fiskalizacja 1
 *      - "Nie"     -> fiskalizacja -1 + opis bledu (na liscie "Ponow fiskalizacje")
 *      - "Nie wiem"-> nic, znacznik ZOSTAJE (okno wroci przy nastepnym starcie)
 *
 * Nigdy nie ustawia fiskalizacja=1 bez klikniecia czlowieka.
 */

export const BLAD_BRAK_WYDRUKU_PO_RESTARCIE =
  'Przerwany druk po restarcie strony - kasjer potwierdzil brak wydruku';
export const ZRODLO_KASJER_PO_RESTARCIE = 'kasjer_po_restarcie';

const useDrukPrzerwany = ({ gotowy = false, poZapisie } = {}) => {
  const [wpis, setWpis] = useState(null);
  const [zajety, setZajety] = useState(false);
  const sprawdzonoRef = useRef(false);

  useEffect(() => {
    if (!gotowy || sprawdzonoRef.current) return undefined;
    sprawdzonoRef.current = true;

    const kandydat = odczytajAktualnyDrukWToku();
    if (!kandydat) return undefined;

    let aktywny = true;
    api.get(`/pos/cart/${kandydat.transactionId}`)
      .then((res) => {
        if (!aktywny) return;
        const tx = res?.data?.data?.transakcja;
        if (!tx) {
          // Odpowiedz bez transakcji - nie zgadujemy, pytanie wroci nastepnym razem.
          return;
        }
        const fisk = Number(tx.fiskalizacja);
        if (fisk === 1) {
          // Potwierdzenie z drukarki jednak dotarlo (albo ktos juz oznaczyl) - nic do roboty.
          wyczyscDrukWToku();
          return;
        }
        setWpis({
          ...kandydat,
          numer: kandydat.numer || tx.numer_paragonu || null,
          kwota: kandydat.kwota ?? (tx.suma_brutto != null
            ? Math.round(((parseFloat(tx.suma_brutto) || 0) - (parseFloat(tx.rabat_kwota) || 0)) * 100) / 100
            : null),
        });
      })
      .catch((err) => {
        if (!aktywny) return;
        if (err?.response?.status === 404) {
          wyczyscDrukWToku();
          return;
        }
        console.warn('[DRUK-W-TOKU] nie udalo sie sprawdzic transakcji - zapytam przy nastepnym starcie:', err?.message);
      });

    return () => { aktywny = false; };
  }, [gotowy]);

  const zapiszDecyzje = useCallback(async (fiskalizacja) => {
    if (!wpis || zajety) return false;
    setZajety(true);
    try {
      const body = { fiskalizacja, zrodlo: ZRODLO_KASJER_PO_RESTARCIE };
      if (fiskalizacja === -1) body.blad = BLAD_BRAK_WYDRUKU_PO_RESTARCIE;
      const res = await api.put(`/transactions/${wpis.transactionId}/fiscal-status`, body);
      if (res?.data?.success === false) {
        throw new Error(res.data.message || res.data.error || 'Serwer odrzucil zmiane statusu');
      }
      wyczyscDrukWToku();
      const numer = wpis.numer || `#${wpis.transactionId}`;
      if (fiskalizacja === 1) {
        toast.success(`Paragon ${numer} oznaczony jako zafiskalizowany (potwierdzenie kasjera).`);
      } else {
        toast(`Paragon ${numer} oznaczony jako niewydrukowany - na liscie transakcji uzyj "Ponow fiskalizacje".`,
          { icon: '⚠️', duration: 10000 });
      }
      setWpis(null);
      if (typeof poZapisie === 'function') poZapisie(wpis.transactionId, fiskalizacja);
      return true;
    } catch (err) {
      console.error('[DRUK-W-TOKU] blad zapisu decyzji kasjera:', err);
      toast.error(`Nie udalo sie zapisac statusu paragonu: ${err?.response?.data?.error || err?.message || 'blad'}. Sprobuj ponownie.`,
        { duration: 10000 });
      return false;
    } finally {
      setZajety(false);
    }
  }, [wpis, zajety, poZapisie]);

  const potwierdzWydruk = useCallback(() => zapiszDecyzje(1), [zapiszDecyzje]);
  const potwierdzBrakWydruku = useCallback(() => zapiszDecyzje(-1), [zapiszDecyzje]);

  // "Nie wiem, sprawdze": zamykamy okno, znacznik zostaje - wroci przy nastepnym starcie.
  const odlozNaPozniej = useCallback(() => {
    setWpis(null);
  }, []);

  return { wpis, zajety, potwierdzWydruk, potwierdzBrakWydruku, odlozNaPozniej };
};

export default useDrukPrzerwany;
